import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Replace with your actual endpoints
const API_URL = `${process.env.REACT_APP_API_FOLDER}list_upcoming_workshops/`;
const API_URL2 = `${process.env.REACT_APP_API_FOLDER}save_preregistration/`;
const API_TOKEN = process.env.REACT_APP_API_TOKEN;

// SWR fetcher
const fetcher = url =>
  fetch(url, { headers: { 'X-API-TOKEN': API_TOKEN } })
    .then(res => {
      if (!res.ok) throw new Error(`Network response was not ok: ${res.status}`);
      return res.json();
    });

// Gradient pairs to cycle through
const gradients = [
  ['#009FE3', '#E6007E'],
  ['#FBF299', '#E6007E'],
  ['#E6007E', '#E24259'],
  ['#E6007E', '#FBF299'],
  ['#6EBC86', '#009FE3'],
  ['#6EBC86', '#FBF299'],
];

// Helper to convert hex to rgba with alpha
function hexToRGBA(hex, alpha = 0.1) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Pre-registration form component for individual workshop
function PreRegistrationForm({ workshopId, onSuccess }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(API_URL2, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-TOKEN': API_TOKEN,
        },
        body: JSON.stringify({ email, workshop_id: workshopId }),
      });
      if (!response.ok) throw new Error(`Registration failed: ${response.status}`);
      setSubmitted(true);
      onSuccess(workshopId);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return <h5 style={{ color: 'green', marginTop: '8px' }}>Thanks! Please check your email, and follow the link to confirm your pre-registration.</h5>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div
        className="newsletter-form-input"
        style={{ display: 'flex', maxWidth: '400px', gap: '8px', marginTop: '8px' }}
      >
        <input
          type="email"
          name="email"
          placeholder="Your email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          style={{ flex: 1 }}
        />
        <button
          type="submit"
          className="newsletter-submit"
          disabled={!email || loading}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundColor: hover ? 'rgba(144,238,144,0.5)' : undefined,
            transition: 'background-color 0.2s ease',
          }}
        >
          {loading ? 'Registering...' : 'Pre-register'}
        </button>
      </div>
    </form>
  );
}

export default function UpcomingDetailsTab() {
  // Fetch workshops; default to empty array
  const { data, error, isLoading } = useSWR(API_URL, fetcher);
  const workshops = Array.isArray(data) ? data : [];

  // Keep track of which workshops have been submitted
  const [submittedWorkshops, setSubmittedWorkshops] = useState([]);
  const handleSuccess = id => {
    setSubmittedWorkshops(prev => [...prev, id]);
  };

  // State and listener for mobile detection
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 600 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Loading skeleton
  if (isLoading || data === undefined) {
    return (
      <div
        className="tab-pane fade show active package-info-tab"
        id="pill-body1"
        role="tabpanel"
        aria-labelledby="pills-package1"
      >
        <div className="workshop_bodytext">
          <Skeleton height={20} width={400} style={{ marginBottom: 16 }} />
          <Skeleton count={2} height={16} width={600} style={{ marginBottom: 24 }} />
          {[...Array(2)].map((_, i) => (
            <div key={i} style={{ marginBottom: 32 }}>
              <Skeleton height={28} width={300} style={{ marginBottom: 8 }} />
              <Skeleton count={3} height={14} width={500} style={{ marginBottom: 12 }} />
              <div
                className="newsletter-form-input"
                style={{ display: 'flex', gap: 8, maxWidth: '400px' }}
              >
                <Skeleton height={36} width={220} />
                <Skeleton height={36} width={120} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return <p className="text-danger">Error: {error.message}</p>;
  }

  return (
    <div
      className="tab-pane fade show active package-info-tab"
      id="pill-body1"
      role="tabpanel"
      aria-labelledby="pills-package1"
    >
      {/* Shimmer keyframes */}
      <style>{`@keyframes shimmer { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }`}</style>
      <div className="workshop_bodytext">
        <p>
          Our workshops frequently sell out fast, so here's an easy way to get in first: <b>pre-register your interest</b>, and when your dream workshop goes live, we&rsquo;ll drop you a line.
        </p>

        {workshops.map((workshop, index) => {
          const [startColor, endColor] = gradients[index % gradients.length];
          const isSubmitted = submittedWorkshops.includes(workshop.id);
          return (
            <div
              key={workshop.id}
              style={{
                background: `linear-gradient(to right, ${hexToRGBA(startColor)}, ${hexToRGBA(endColor)})`,
                border: `1px solid ${startColor}`,
                borderRadius: '8px',
                padding: '5px 30px 30px 30px',
                marginTop: '24px',
                display: 'inline-block',
                width: isMobile ? '100%' : 'fit-content',
                maxWidth: '100%',
                animation: isSubmitted ? 'shimmer 1.5s ease-in-out' : undefined,
              }}
            >
              <h4 className="funding_title">{workshop.title}</h4>
              <i>{workshop.ctype} workshop, happening between {workshop.date_span_start} and {workshop.date_span_end}</i>
              <p>{workshop.details}</p>
              <PreRegistrationForm workshopId={workshop.id} onSuccess={handleSuccess} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
